import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, MessageCircle, Play, Layers } from 'lucide-react';
import { Post } from '../../types';

interface DiscoverGridProps {
  posts: Post[];
}

export const DiscoverGrid: React.FC<DiscoverGridProps> = ({ posts }) => {
  if (posts.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-md p-8 text-center">
        <div className="mb-4">
          <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
            <Heart className="h-8 w-8 text-gray-400" />
          </div>
        </div>
        <h3 className="text-xl font-medium text-gray-900 mb-2">No posts found</h3>
        <p className="text-gray-500">Try exploring different categories or check back later</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-1 md:gap-4">
      {posts.map((post) => {
        // Get the first media item or fallback to legacy imageUrl
        const firstMedia = post.media && post.media.length > 0 ? post.media[0] : null;
        const imageUrl = firstMedia?.media_url || (post as any).imageUrl;
        const isVideo = firstMedia?.media_type === 'video';
        const hasMultipleMedia = post.media && post.media.length > 1;
        const likeCount = post.like_count !== undefined ? post.like_count : (post as any).likeCount || 0;
        const commentCount = post.comment_count !== undefined ? post.comment_count : (post as any).commentCount || 0;

        return (
          <Link 
            to={`/post/${post.id}`} 
            key={post.id} 
            className="relative pb-[100%] bg-gray-100 overflow-hidden group block"
          >
            {imageUrl ? (
              <>
                {isVideo ? (
                  <video
                    src={imageUrl}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    muted
                  />
                ) : (
                  <img 
                    src={imageUrl} 
                    alt="Discover" 
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    onError={(e) => {
                      console.error('Discover grid image failed to load:', imageUrl);
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}

                {/* Media Type Indicators */}
                <div className="absolute top-2 right-2 flex space-x-1">
                  {isVideo && (
                    <div className="bg-black bg-opacity-50 rounded-full p-1">
                      <Play className="h-3 w-3 text-white fill-white" />
                    </div>
                  )}
                  {hasMultipleMedia && (
                    <div className="bg-black bg-opacity-50 rounded-full p-1">
                      <Layers className="h-3 w-3 text-white" />
                    </div>
                  )}
                </div>

                {/* Media Count Badge */}
                {hasMultipleMedia && (
                  <div className="absolute top-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                    {post.media.length}
                  </div>
                )}

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex items-center space-x-4 text-white">
                    <div className="flex items-center">
                      <Heart className="h-5 w-5 mr-1 fill-white" />
                      <span className="font-semibold">{likeCount}</span>
                    </div>
                    <div className="flex items-center">
                      <MessageCircle className="h-5 w-5 mr-1 fill-white" />
                      <span className="font-semibold">{commentCount}</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-gray-400 bg-gray-100">
                <p className="text-sm">No media</p>
              </div>
            )}
          </Link>
        );
      })}
    </div>
  );
};